import { HttpAgent } from "@ag-ui/client";
import {
  Button,
  ChatMessage,
  Drawer,
  IconButton,
  InputField,
  Note
} from "@appretio/ui-kit";
import { useEffect, useRef, useState } from "react";
import * as styles from "./Chatbot.css";
import { useChat } from "./ChatContext";
import { createChatAgent } from "./hooks/chatAgent";
import { useConversation } from "./hooks/useConversation";
import { useSendMessage } from "./hooks/useSendMessage";

interface ChatUiMessage {
  role: "user" | "assistant";
  content: string;
}

const GREETING =
  "Hi! I'm the **Appretio Assistant**. Ask me a question about Appretio or " +
  "merchandising, or tell me a feature request for the product team.";

/**
 * App-wide chatbot: a slide-in drawer opened from the app header.
 * Mounted once inside <ChatProvider> — open state lives there; this component
 * owns the conversation transcript and the draft.
 */
export function Chatbot() {
  const { isOpen, close } = useChat();
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState<ChatUiMessage[]>([]);
  // The AG-UI agent owns the conversation — thread id, message history and
  // state all live on it, so there's nothing to thread between turns here.
  // A ref (not state) because replacing it must not re-render on its own.
  const agentRef = useRef<HttpAgent | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);

  const conversation = useConversation({ enabled: isOpen });
  const sendMessage = useSendMessage();

  // Keep the transcript pinned to the latest message: jump to the bottom on
  // every new message and when the drawer opens. Between updates the container
  // scrolls freely, so the user can read back through history.
  const messagesRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isOpen]);

  // Seed the drawer from the user's latest conversation exactly once. A ref
  // (not message/conversation state) gates it, so resetting that state via
  // "New chat" — or starting a thread — doesn't re-trigger a re-seed.
  const hasSeeded = useRef(false);
  useEffect(() => {
    if (hasSeeded.current || !conversation.data) return;
    const turns = conversation.data.turns ?? [];
    if (!turns.length) return;
    hasSeeded.current = true;
    // Replay the stored turns so the model sees the history the user can see,
    // reusing the stored conversation id as the thread id — otherwise the next
    // turn opens a second conversation and the replayed one stops being the
    // "latest", losing this history on the following reopen.
    agentRef.current = createChatAgent(
      turns,
      conversation.data.conversation_id
    );
    setMessages(
      turns.flatMap((turn) => [
        { role: "user" as const, content: turn.prompt },
        { role: "assistant" as const, content: turn.response }
      ])
    );
  }, [conversation.data]);

  // Built on first use rather than in the render body: `useRef` evaluates its
  // argument every render, and this component re-renders on every keystroke.
  function agent(): HttpAgent {
    agentRef.current ??= createChatAgent();
    return agentRef.current;
  }

  // Append a streamed chunk to the in-flight assistant bubble, which is always
  // the last message (a single stream runs at a time). Functional update so
  // concurrent deltas accumulate correctly.
  function appendToBubble(text: string) {
    setMessages((prev) =>
      prev.map((message, i) =>
        i === prev.length - 1
          ? { ...message, content: message.content + text }
          : message
      )
    );
  }

  function handleSend() {
    const prompt = draft.trim();
    if (!prompt || isStreaming) return;

    // The in-memory thread is now authoritative; never re-seed over it.
    hasSeeded.current = true;
    // Push the user turn plus an empty assistant bubble the stream grows into.
    setMessages((prev) => [
      ...prev,
      { role: "user", content: prompt },
      { role: "assistant", content: "" }
    ]);
    setDraft("");
    setIsStreaming(true);

    sendMessage(agent(), prompt, appendToBubble)
      .catch((error) => {
        console.error("chat stream failed", error);
        appendToBubble(
          "\n\n_The assistant could not respond. Please try again._"
        );
      })
      .finally(() => setIsStreaming(false));
  }

  function handleNewChat() {
    hasSeeded.current = true; // a deliberate reset must not be re-seeded
    setMessages([]);
    agentRef.current = createChatAgent(); // new agent = new thread id
    setDraft("");
  }

  return (
    <Drawer
      open={isOpen}
      onClose={close}
      width="480px"
      closeLabel="Close chat"
      data-testid="chat-drawer"
      aria-label="Appretio Assistant"
    >
      <div className={styles.header}>
        <span className={styles.headerTitle}>Appretio Assistant (Beta)</span>
        <div className={styles.headerActions}>
          {/* No close button: the drawer dismisses by clicking outside it.
              New chat stays — it resets the thread, which the backdrop can't. */}
          <IconButton
            aria-label="New chat"
            iconName="add"
            onClick={handleNewChat}
          />
        </div>
      </div>

      <Note
        className={styles.beta}
        size="compact"
        variant="warning"
        text="Beta — your messages are recorded and shared with the product team."
      />

      <div ref={messagesRef} className={styles.messages}>
        {/* Persistent intro: stays at the top of the thread, not just an
            empty-state placeholder. */}
        <ChatMessage role="assistant" content={GREETING} />
        {messages.map((message, index) => (
          <ChatMessage
            key={index}
            role={message.role}
            content={message.content}
          />
        ))}
      </div>

      <div className={styles.inputRow}>
        <InputField
          className={styles.inputField}
          aria-label="Message"
          placeholder="Ask or request a feature…"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              handleSend();
            }
          }}
        />
        <Button
          type="button"
          isLoading={isStreaming}
          disabled={!draft.trim()}
          onClick={handleSend}
        >
          Send
        </Button>
      </div>
    </Drawer>
  );
}
